import { ContentDocument } from './contentApi';
import { ContactSubmission } from './contactApi';
import { msalInstance } from '../App';
import { apiRequest } from '../auth/msalConfig';

export interface AdminBooking {
  id: string;
  appointmentTypeId?: string;
  startUtc: string;
  endUtc: string;
  timeZoneId?: string;
  status: string;
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
  conflict: boolean;
  outlook?: { syncStatus: string; lastError?: string };
  crm?: { syncStatus: string; contactSubmissionId?: string };
  /**
   * Cosmos partition key, format "{resourceId}|{yyyy-MM-dd}" (booking's *local* date, practice
   * timezone). Use this (not a date derived from startUtc in the admin's own browser timezone)
   * to build the "d" query parameter for status/delete calls — deriving it from startUtc breaks
   * whenever the admin's machine isn't in the practice's timezone.
   */
  dayKey: string;
}

export interface OutlookStatus {
  configured: boolean;
  status: string;
  hasValidToken?: boolean;
  accountUsername?: string;
  bookingCalendarName?: string;
  blockingCalendarCount?: number;
  idType?: string;
  lastSyncAt?: string;
  lastSyncError?: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:7016/api';

async function adminHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  try {
    if (msalInstance) {
      const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
      if (account) {
        const response = await msalInstance.acquireTokenSilent({
          ...apiRequest,
          account: account,
        });
        headers['Authorization'] = `Bearer ${response.accessToken}`;
      }
    }
  } catch (error) {
    console.warn('Failed to acquire MSAL token:', error);
  }

  return headers;
}

/**
 * Admin: Get all content items of a type (including unpublished).
 */
export async function adminGetContentByType(type: string): Promise<ContentDocument[]> {
  const response = await fetch(`${API_URL}/cms-admin/content/${type}`, {
    headers: await adminHeaders(),
  });
  if (!response.ok) throw new Error(`Failed to fetch admin content: ${response.status}`);
  return response.json();
}

/**
 * Admin: Get a single content item by type and id.
 */
export async function adminGetContentById(type: string, id: string): Promise<ContentDocument | null> {
  const response = await fetch(`${API_URL}/cms-admin/content/${type}/${id}`, {
    headers: await adminHeaders(),
  });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
  return response.json();
}

/**
 * Admin: Create a new content item.
 */
export async function adminCreateContent(document: Partial<ContentDocument>): Promise<ContentDocument> {
  const response = await fetch(`${API_URL}/cms-admin/content`, {
    method: 'POST',
    headers: await adminHeaders(),
    body: JSON.stringify(document),
  });
  if (!response.ok) {
    const err = await response.text();
    throw new Error(err || `Create failed: ${response.status}`);
  }
  return response.json();
}

/**
 * Admin: Update an existing content item.
 */
export async function adminUpdateContent(type: string, id: string, document: Partial<ContentDocument>): Promise<ContentDocument> {
  const response = await fetch(`${API_URL}/cms-admin/content/${type}/${id}`, {
    method: 'PUT',
    headers: await adminHeaders(),
    body: JSON.stringify(document),
  });
  if (!response.ok) {
    const err = await response.text();
    throw new Error(err || `Update failed: ${response.status}`);
  }
  return response.json();
}

/**
 * Admin: Delete a content item.
 */
export async function adminDeleteContent(type: string, id: string): Promise<void> {
  const response = await fetch(`${API_URL}/cms-admin/content/${type}/${id}`, {
    method: 'DELETE',
    headers: await adminHeaders(),
  });
  if (!response.ok) throw new Error(`Delete failed: ${response.status}`);
}

/**
 * Admin: Reorder content items by patching only sortOrder on the backend.
 * Sends a single request instead of N full-document updates, minimising Cosmos RU usage.
 */
export async function adminReorderContent(type: string, items: { id: string; sortOrder: number }[]): Promise<void> {
  const response = await fetch(`${API_URL}/cms-admin/content/${type}/reorder`, {
    method: 'POST',
    headers: await adminHeaders(),
    body: JSON.stringify(items),
  });
  if (!response.ok) {
    const err = await response.text();
    throw new Error(err || `Reorder failed: ${response.status}`);
  }
}

/**
 * Admin: Upload a media file.
 */
export async function adminUploadMedia(file: File): Promise<{ url: string; blobName: string }> {
  const formData = new FormData();
  formData.append('file', file);

  const headers = await adminHeaders();
  // Remove Content-Type for FormData (let browser set it with boundary)
  delete headers['Content-Type'];

  const response = await fetch(`${API_URL}/cms-admin/media`, {
    method: 'POST',
    headers: headers,
    body: formData,
  });
  if (!response.ok) {
    const err = await response.text();
    throw new Error(err || `Upload failed: ${response.status}`);
  }
  return response.json();
}

/**
 * Admin: List all uploaded media files.
 */
export async function adminListMedia(): Promise<Array<{ name: string; url: string; contentType: string; size: number }>> {
  const response = await fetch(`${API_URL}/cms-admin/media`, {
    headers: await adminHeaders(),
  });
  if (!response.ok) throw new Error(`List media failed: ${response.status}`);
  return response.json();
}

/**
 * Admin: Delete a media file by blob name.
 */
export async function adminDeleteMedia(blobName: string): Promise<void> {
  const response = await fetch(`${API_URL}/cms-admin/media/${encodeURIComponent(blobName)}`, {
    method: 'DELETE',
    headers: await adminHeaders(),
  });
  if (!response.ok) {
    const err = await response.text();
    throw new Error(err || `Delete failed: ${response.status}`);
  }
}

/**
 * Admin: Get all contact form submissions.
 */
export async function adminGetContactSubmissions(): Promise<ContactSubmission[]> {
  const response = await fetch(`${API_URL}/cms-admin/contact-submissions`, {
    headers: await adminHeaders(),
  });
  if (!response.ok) throw new Error(`Failed to fetch submissions: ${response.status}`);
  return response.json();
}

/**
 * Admin: Update the status of a contact submission.
 */
export async function adminUpdateContactSubmissionStatus(id: string, status: string): Promise<ContactSubmission> {
  const response = await fetch(`${API_URL}/cms-admin/contact-submissions/${id}/status`, {
    method: 'PUT',
    headers: await adminHeaders(),
    body: JSON.stringify({ status }),
  });
  if (!response.ok) throw new Error(`Failed to update status: ${response.status}`);
  return response.json();
}

/**
 * Admin: Delete a contact submission.
 */
export async function adminDeleteContactSubmission(id: string): Promise<void> {
  const response = await fetch(`${API_URL}/cms-admin/contact-submissions/${id}`, {
    method: 'DELETE',
    headers: await adminHeaders(),
  });
  if (!response.ok) throw new Error(`Failed to delete submission: ${response.status}`);
}

/**
 * Admin: Send a test email using a dummy ContactSubmission.
 * type: "notification" | "confirmation" | "both"
 */
export async function adminSendTestEmail(params: {
  type: 'notification' | 'confirmation' | 'both';
  lang: string;
  areaOfLaw: string;
  recipientEmail: string;
}): Promise<{ sent: string[]; recipient: string; lang: string; areaOfLaw: string }> {
  const response = await fetch(`${API_URL}/cms-admin/email/test`, {
    method: 'POST',
    headers: await adminHeaders(),
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const err = await response.text();
    throw new Error(err || `Test email failed: ${response.status}`);
  }
  return response.json();
}

/**
 * Admin: Download a submission file attachment via the authenticated backend.
 * The blobPath format is "{submissionId}/{guid}-{filename}".
 * Triggers a browser download by creating a temporary object URL.
 */
export async function adminDownloadSubmissionFile(submissionId: string, blobPath: string): Promise<void> {
  // Strip the leading "{submissionId}/" prefix — the backend route already has /{id}/ in it
  const blobName = blobPath.startsWith(`${submissionId}/`)
    ? blobPath.slice(submissionId.length + 1)
    : blobPath;

  const response = await fetch(
    `${API_URL}/cms-admin/contact-submissions/${encodeURIComponent(submissionId)}/files/${encodeURIComponent(blobName)}`,
    { headers: await adminHeaders() }
  );
  if (!response.ok) throw new Error(`Download failed: ${response.status}`);

  const blob = await response.blob();
  const contentDisposition = response.headers.get('content-disposition') ?? '';
  const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  const filename = filenameMatch ? filenameMatch[1].replace(/['"]/g, '') : blobName;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Booking admin ──────────────────────────────────────────────────────

/**
 * Extracts the booking's local date from its dayKey ("{resourceId}|{yyyy-MM-dd}") — this is the
 * date the backend partitions on. Deriving it from startUtc in the browser's local timezone would
 * give the wrong Cosmos partition (and a 404) whenever the admin isn't in the practice's timezone.
 */
function localDateOf(booking: AdminBooking): string {
  const idx = booking.dayKey.lastIndexOf('|');
  return idx >= 0 ? booking.dayKey.slice(idx + 1) : booking.dayKey;
}

/**
 * Admin: list bookings in a UTC date range, optionally filtered by status.
 */
export async function adminGetBookings(from: string, to: string, status?: string): Promise<AdminBooking[]> {
  const params = new URLSearchParams({ from, to });
  if (status) params.set('status', status);
  const response = await fetch(`${API_URL}/cms-admin/bookings?${params.toString()}`, {
    headers: await adminHeaders(),
  });
  if (!response.ok) throw new Error(`Failed to fetch bookings: ${response.status}`);
  return response.json();
}

/**
 * Admin: confirm/decline/cancel a booking. 'd' (local date) is derived from startUtc automatically.
 */
export async function adminUpdateBookingStatus(booking: AdminBooking, status: 'confirmed' | 'declined' | 'cancelled'): Promise<AdminBooking> {
  const d = localDateOf(booking);
  const response = await fetch(`${API_URL}/cms-admin/bookings/${encodeURIComponent(booking.id)}/status?d=${d}`, {
    method: 'PUT',
    headers: await adminHeaders(),
    body: JSON.stringify({ status }),
  });
  if (!response.ok) {
    const err = await response.text();
    throw new Error(err || `Failed to update booking: ${response.status}`);
  }
  return response.json();
}

export async function adminDeleteBooking(booking: AdminBooking): Promise<void> {
  const d = localDateOf(booking);
  const response = await fetch(`${API_URL}/cms-admin/bookings/${encodeURIComponent(booking.id)}?d=${d}`, {
    method: 'DELETE',
    headers: await adminHeaders(),
  });
  if (!response.ok) throw new Error(`Failed to delete booking: ${response.status}`);
}

export interface BlockRequest {
  date: string; // yyyy-MM-dd, local
  startTime: string; // HH:mm, local
  endTime: string; // HH:mm, local
  reason?: string;
}

/**
 * Admin: create an internal time block (vacation, admin-only appointment, etc.) — uses the same
 * locking guarantee as a real booking, so it can never silently overlap one.
 */
export async function adminBlockBooking(block: BlockRequest): Promise<AdminBooking> {
  const response = await fetch(`${API_URL}/cms-admin/bookings/block`, {
    method: 'POST',
    headers: await adminHeaders(),
    body: JSON.stringify(block),
  });
  if (!response.ok) {
    const err = await response.text();
    throw new Error(err || `Failed to create block: ${response.status}`);
  }
  return response.json();
}

export async function adminGetOutlookStatus(): Promise<OutlookStatus> {
  const response = await fetch(`${API_URL}/cms-admin/outlook/status`, { headers: await adminHeaders() });
  if (!response.ok) throw new Error(`Failed to fetch Outlook status: ${response.status}`);
  return response.json();
}

export async function adminConnectOutlook(): Promise<{ authorizationUrl: string }> {
  const response = await fetch(`${API_URL}/cms-admin/outlook/connect`, { method: 'POST', headers: await adminHeaders() });
  if (!response.ok) throw new Error(`Failed to start Outlook connection: ${response.status}`);
  return response.json();
}

export async function adminDisconnectOutlook(): Promise<void> {
  const response = await fetch(`${API_URL}/cms-admin/outlook/disconnect`, { method: 'POST', headers: await adminHeaders() });
  if (!response.ok) throw new Error(`Failed to disconnect Outlook: ${response.status}`);
}

export async function adminResyncOutlook(): Promise<void> {
  const response = await fetch(`${API_URL}/cms-admin/outlook/resync`, { method: 'POST', headers: await adminHeaders() });
  if (!response.ok) throw new Error(`Failed to resync Outlook: ${response.status}`);
}
